import i18n from 'i18next';
import { DefaultValues, FieldValues, UseFormReturn } from 'react-hook-form';
import { useEffect } from 'react';
import { RegisterOptions } from 'react-hook-form/dist/types/validator';
import superjson from 'superjson';
import { isValid } from 'date-fns';

export function useResetFormWhenDataIsLoaded<
  TFieldValues extends FieldValues,
  // eslint-disable-next-line @typescript-eslint/ban-types
  TContext extends object = object,
>(
  form: UseFormReturn<TFieldValues, TContext>,
  defaultValues: TFieldValues | undefined,
) {
  useEffect(() => {
    if (defaultValues) {
      // { ...defaultValues } is required to not modify original defaultValues
      const clonedValues = superjson.parse<DefaultValues<TFieldValues>>(
        superjson.stringify(defaultValues),
      );
      form.reset(clonedValues, {
        keepDirtyValues: true,
      });
    }
  }, [defaultValues]);
}

export const emailRegexp =
  /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*)@(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
export const usernameRegexp = /^[a-zA-Z0-9.@!#$%&'*+/=?^_`{|}~-]*$/;
export const phoneRegexp =
  /^(\s*)?(\+)?([- _():=+]?\d[- _():=+]?){5,14}(\s*)?$/;

export function requiredRule(condition: boolean = true) {
  return {
    required: {
      value: condition,
      message: i18n.t('translation:errors.required'),
    },
  };
}

export function emailRule() {
  return {
    pattern: {
      value: emailRegexp,
      message: i18n.t('translation:errors.email'),
    },
  };
}

export function usernameRule() {
  return {
    pattern: {
      value: usernameRegexp,
      message: i18n.t('translation:errors.username'),
    },
  };
}

export function maxRule(max: number) {
  return {
    max: {
      value: max,
      message: i18n.t('translation:errors.max', { max: max }),
    },
  };
}
export function minRule(min: number) {
  return {
    min: {
      value: min,
      message: i18n.t('translation:errors.min', { min: min }),
    },
  };
}
export function positiveRule() {
  return {
    min: {
      value: 0,
      message: i18n.t('translation:errors.positive'),
    },
  };
}
export function maxLengthRule(length: number) {
  return {
    maxLength: {
      value: length,
      message: i18n.t('translation:errors.max_length', {
        maxLength: length,
      }),
    },
  };
}
export function minLengthRule(length: number) {
  return {
    minLength: {
      value: length,
      message: i18n.t('translation:errors.min_length', {
        minLength: length,
      }),
    },
  };
}
export function phoneRule(): RegisterOptions {
  return {
    pattern: {
      value: phoneRegexp,
      message: i18n.t('translation:errors.phone'),
    },
  };
}
export function exactLengthRule(length: number): RegisterOptions {
  return {
    validate: (data: string) =>
      data.length === length ||
      (i18n.t('translation:errors.exact_length', {
        length: length,
      }) as string),
  };
}

export function forbidSpacesRule(): RegisterOptions {
  return {
    pattern: {
      value: /^\S*$/,
      message: i18n.t('translation:errors.no_spaces'),
    },
  };
}

export function requiredDate(): RegisterOptions {
  return {
    required: i18n.t('translation:errors.required'),
    validate: (value) =>
      isValid(value) || i18n.t('translation:errors.invalid_date'),
  };
}
